<template>
  <div class="grid-container" :class="mdAndDown ? 'grid-container-mobile' : 'grid-container-desktop'">
    <div v-for="chart in apexWithCommonSettings" :key="chart.id" class="grid-item">
      <telemetrie-chart
        :graph-icon="chart.graphIcon"
        :graph-title="chart.graphTitle"
        :graph-description="chart.graphDescription"
        :graph-type="chart.graphType"
        :chart-options="chart.chartOptions"
        :chart-data="chart.series"
        :parameter-ranges="chart.parameterRanges"
        :is-percentage="chart.isPercentage"
      ></telemetrie-chart>
    </div>
  </div>
</template>

<script>
import TelemetrieChart from '@/modules/telemetrie/components/TelemetrieChart.vue';
import { mapState } from 'vuex';
import dateUtils from '@/common/dateUtils';
import common from '@/mixins/common';

const apexChartOptions = {
  chart: {
    toolbar: {
      show: false,
    },
    height: '100%',
    animations: {
      enabled: true,
      dynamicAnimation: {},
    },
  },
  markers: {
    size: 0,
  },
  stroke: {
    width: 2,
    curve: 'straight',
  },
  dataLabels: {
    enabled: false,
  },
  tooltip: {
    enabled: false,
  },
  plotOptions: {
    bar: {
      horizontal: false,
      distributed: false,
    },
  },
  colors: ['#0066ff', '#999999'],
  legend: {
    show: true,
    labels: {
      colors: '',
    },
    onItemClick: {
      toggleDataSeries: false,
    },
    onItemHover: {
      highlightDataSeries: false,
    },
  },
  xaxis: {
    type: 'numeric',
    tickPlacement: 'on',
    tickAmount: 2,
    labels: {
      show: true,
      rotate: 0,
      showDuplicates: true,
      trim: false,
      hideOverlappingLabels: true,
      style: {
        fontSize: '10px',
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: '',
      },
    },
  },
};

export default {
  name: 'TelemetrieGraphGrid',
  components: {
    TelemetrieChart,
  },
  mixins: [common],
  methods: {
    // for single dates
    barGraphData(relatedValues) {
      const values = Array.from(relatedValues, (value) => value.value);
      const averages = Array.from(relatedValues, (value) => value.average);

      return {
        values,
        averages,
      };
    },

    // for multiple dates (range)
    lineGraphData(relatedValues) {
      const values = Array.from(relatedValues, (value) => ({
        x: dateUtils.reformatDateIsoStringAsYYYYMMDD(value.date),
        y: value.value,
      }));
      const averages = Array.from(relatedValues, (value) => ({
        x: dateUtils.reformatDateIsoStringAsYYYYMMDD(value.date),
        y: value.average,
      }));
      //console.debug('values', JSON.stringify(values, null, 2));ng
      return {
        values,
        averages,
      };
    },
  },
  computed: {
    ...mapState('telemetrie', ['telemetrieParameters', 'telemetrieValues']),
    apexWithCommonSettings() {
      let data = this.apexData.map((chart, index) => {
        const stroke_width = chart.graphType === 'bar' ? 0 : 2;
        const textColor = this.themeName === 'dark' ? '#fff' : '#000';
        apexChartOptions.legend.labels.colors = textColor;
        apexChartOptions.yaxis.labels.style.colors = textColor;
        const chartOptions = {
          ...apexChartOptions,
          stroke: {
            width: stroke_width,
          },
          noData: {
            text: this.$t('no_data'),
            style: {
              color: textColor,
            },
          },
          chart: {
            ...apexChartOptions.chart,
            id: 'telemetrie-' + (index + 1),
          },
        };

        return {
          ...chart,
          chartOptions,
        };
      });
      console.debug('apexData', data);
      return data;
    },
    dateFrom() {
      return this.$route.params.from;
    },
    dateTo() {
      return this.$route.params.to;
    },
    apexData() {
      const selectedDate = { from: this.dateFrom, to: this.dateTo };
      return this.telemetrieParameters
        .map((parameter) => {
          const relatedValues = this.telemetrieValues.filter((value) => {
            const valueDate = dateUtils.reformatDateIsoStringAsYYYYMMDD(value.date);
            // filter through all the dates and include the right ones and if it's an array only include the ones between the dates of the range
            if (selectedDate.from !== selectedDate.to) {
              return value.keyIdFk === parameter.id && valueDate >= selectedDate.from && valueDate <= selectedDate.to;
            }
            return value.keyIdFk === parameter.id && valueDate === selectedDate.from;
          });
          const uniqueDates = [...new Set(relatedValues.map((value) => value.date))];
          const graphType = uniqueDates.length > 1 ? 'line' : 'bar';

          let graphData;

          if (Object.keys(selectedDate).length !== 0) {
            graphData = this.lineGraphData(relatedValues);
          } else {
            graphData = this.barGraphData(relatedValues);
          }
          return {
            sort: parameter.sort,
            graphType: graphType,
            graphTitle: parameter.title,
            graphDescription: parameter.description,
            graphIcon: parameter.icon,
            parameterRanges: {
              min: parameter.min,
              max: parameter.max,
              minOk: parameter.min_ok,
              maxOk: parameter.max_ok,
              minWarning: parameter.min_warning,
              maxWarning: parameter.max_warning,
              minError: parameter.min_error,
              maxError: parameter.max_error,
            },
            isPercentage: parameter.is_percentage,
            series: [
              {
                name: this.$t('value'),
                data: graphData.values,
              },
              {
                name: this.$t('average'),
                data: graphData.averages,
              },
            ],
          };
        })
        .sort((chartA, chartB) => chartA.sort - chartB.sort);
    },
  },
};
</script>

<style scoped>
.grid-container {
  display: grid;
  padding: 10px;
  gap: 10px;
  align-content: start;
}

.grid-item {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.grid-container-desktop {
  grid-template-columns: repeat(auto-fit, minmax(525px, 1fr));
}

.grid-container-mobile {
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}
</style>
